<template>
  <section class="enterprise-page">
    <CreateDialog
      ref="createDialogRef"
      @addMemberCall="refreshMembers"
      @editMemberCall="refreshMembers"
      @deleteMemberCall="refreshMembers"
      @dissolveEnterpriseCall="handleDissolveEnterpriseCall"
      @quitEnterpriseCall="handleQuitEnterpriseCall"
    />
    <AddMemberDialog ref="addMemberDialogRef" @saved="refreshMembers" />

    <header class="enterprise-toolbar">
      <h1 class="enterprise-title">
        {{ enterpriseName ? $t(isGlobalTeam ? 'enterprise_ui_global_enterprise_title' : 'enterprise_ui_enterprise_title', { name: enterpriseName }) : $t('account_enterprise_manager') }}
      </h1>

      <div v-if="canInviteOrAddMembers || canDissolveEnterprise" class="enterprise-actions">
        <button v-if="canInviteOrAddMembers" class="enterprise-button enterprise-button-primary" type="button" @click="handleAddMember">
          {{ $t('account_enterprise_add_member') }}
        </button>
        <button
          v-if="canDissolveEnterprise"
          class="enterprise-button enterprise-button-danger"
          type="button"
          @click="handleDissolveEnterprise"
        >
          {{ $t('account_enterprise_dissolve_title') }}
        </button>
      </div>
    </header>

    <template v-if="invitedInfo.inviteBizTeamId">
      <div class="invited-enterprise-card">
        <span class="invited-enterprise-icon">
          <WorkbenchIcon name="building-2" :size="30" />
        </span>
        <h2>“{{ invitedInfo.bizTeamName }}” {{ $t('account_enterprise_invited_title') }}</h2>
        <p>{{ $t('enterprise_management_accept_invite_description') }}</p>
        <button
          class="enterprise-button enterprise-button-primary invited-enterprise-button"
          type="button"
          :disabled="joiningEnterprise"
          :aria-busy="joiningEnterprise"
          @click="handleJoinEnterprise"
        >
          <span v-if="joiningEnterprise" class="enterprise-button-spinner" aria-hidden="true" />
          {{ $t(joiningEnterprise ? 'enterprise_management_join_syncing' : joiningRecord ? 'enterprise_management_query_status' : 'account_enterprise_invited_btn_text') }}
        </button>
        <p v-if="joiningEnterprise" class="invited-enterprise-sync-hint">
          {{ $t('enterprise_management_join_syncing_hint') }}
        </p>
        <p v-else-if="joiningRecord" class="invited-enterprise-sync-hint">
          {{ $t('enterprise_management_processing_hint') }}
        </p>
      </div>
    </template>

    <template v-else>
      <div v-if="showSeatSummary" class="enterprise-overview">
        <h2 class="section-title">{{ $t('enterprise_management_plan_info') }}</h2>
        <div class="seat-summary">
          <div class="seat-summary__main">
            <WorkbenchIcon name="users" :size="15" />
            <span>
              <template v-if="isLegacyCross && !legacySeats">{{ $t('enterprise_management_shared_seats') }}{{ workspace.loading.value ? $t('enterprise_management_loading') : $t('enterprise_management_unavailable') }}</template>
              <template v-else>{{ $t(isGlobalTeam ? 'enterprise_ui_seats_global' : isVipSite ? 'enterprise_ui_seats_vip' : 'enterprise_ui_seats_ai', { used: usedSeatCount, total: seatLimit }) }}</template>
            </span>
          </div>
          <small v-if="isLegacyCross && legacySeats" class="legacy-seat-breakdown">{{ legacySeatBreakdown }}</small>
          <button v-if="isLegacyCross && managementAccess.isManager" type="button" class="workspace-refresh" :disabled="workspace.loading.value" @click="workspace.refresh">{{ $t('enterprise_management_query_status') }}</button>
        </div>

        <div v-if="showWorkspaceQuota" class="space-quota-grid">
          <article v-for="region in globalRegions" :key="region" class="quota-card">
            <div class="quota-card__header">
              <span class="quota-card__title">
                <b>{{ $t(region === 'VIP' ? 'enterprise_management_china_pool' : 'enterprise_management_international_pool') }}</b>
                <small>{{ region === 'VIP' ? 'doclingo.vip' : 'doclingo.ai' }}</small>
              </span>
              <strong>{{ formatQuota(globalQuota(region).used) }} / {{ formatQuota(globalQuota(region).total) }}</strong>
            </div>
            <div class="quota-progress"><span :style="{ width: `${globalQuota(region).percent}%` }" /></div>
            <div class="quota-card__footer">
              <span>{{ $t('enterprise_management_fuel_remaining_value', { count: formatQuota(globalQuota(region).packageRemaining) }) }}</span>
              <button v-if="canManageBalanceAlert" type="button" class="quota-link" @click="openBalanceAlert">{{ $t('enterprise_management_alert_settings') }} →</button>
            </div>
          </article>
        </div>
        <div v-else-if="showIndependentQuota" class="space-quota-grid local-quota-grid">
          <article class="quota-card">
            <div class="quota-card__header">
              <span class="quota-card__title">
                <b>{{ $t('enterprise_management_plan_quota') }}</b>
                <small>{{ currentSiteName }}</small>
              </span>
              <strong>{{ formatQuota(independentQuota.serviceUsedWords) }} / {{ formatQuota(independentQuota.serviceTotalWords) }}</strong>
            </div>
            <div class="quota-progress"><span :style="{ width: `${quotaPercent(independentQuota.serviceUsedWords, independentQuota.serviceTotalWords)}%` }" /></div>
            <div class="quota-card__footer">
              <span>{{ $t('enterprise_management_remaining_chars_value', { count: formatQuota(independentQuota.serviceRemainingWords) }) }}</span>
              <button v-if="canManageBalanceAlert" type="button" class="quota-link" @click="openBalanceAlert">{{ $t('enterprise_management_alert_settings') }} →</button>
            </div>
          </article>
          <article class="quota-card">
            <div class="quota-card__header">
              <span class="quota-card__title">
                <b>{{ $t('enterprise_management_fuel_quota') }}</b>
                <small>{{ $t('enterprise_management_fuel_validity') }}</small>
              </span>
              <strong>{{ formatQuota(independentQuota.packageUsedWords) }} / {{ formatQuota(independentQuota.packageTotalWords) }}</strong>
            </div>
            <div class="quota-progress"><span :style="{ width: `${quotaPercent(independentQuota.packageUsedWords, independentQuota.packageTotalWords)}%` }" /></div>
            <div class="quota-card__footer">
              <span>{{ $t('enterprise_management_remaining_chars_value', { count: formatQuota(independentQuota.packageRemainingWords) }) }}</span>
              <button v-if="canPurchaseFuel" type="button" class="quota-link" @click="openEnterprisePlans">{{ $t('enterprise_management_buy_fuel') }} →</button>
            </div>
          </article>
        </div>
      </div>

      <div class="enterprise-table-shell" :class="{ 'is-global': isGlobalTeam }">
        <div class="enterprise-table-scroll">
          <div class="enterprise-table-header enterprise-table-grid">
            <div>{{ $t('enterprise_management_member') }}</div>
            <div>{{ $t('enterprise_management_alias') }}</div>
            <div v-if="isGlobalTeam">{{ $t('enterprise_management_workspace') }}</div>
            <div>{{ $t('enterprise_management_status') }}</div>
            <div>{{ $t('enterprise_management_join_time') }}</div>
            <div>{{ $t('enterprise_management_permissions') }}</div>
            <div>{{ $t('enterprise_management_operations') }}</div>
          </div>

          <div v-if="pagedMembers.length" class="enterprise-table-body">
            <div v-for="member in pagedMembers" :key="memberKey(member)" class="enterprise-table-row enterprise-table-grid">
              <div class="member-cell">
                <span class="member-avatar" :class="`member-avatar-role-${member.roleType}`">
                  <WorkbenchIcon name="user-round" :size="13" />
                </span>
                <span class="member-account" :title="member.userAccount">{{ member.userAccount }}</span>
                <span class="role-tag">{{ roleLabel(member.roleType) }}</span>
              </div>

              <div class="muted-cell" :title="member.aliasName || ''">{{ member.aliasName || '—' }}</div>

              <div v-if="isGlobalTeam">
                <span class="space-tag" :class="memberRegion(member) === 'VIP' ? 'space-tag-vip' : 'space-tag-ai'">
                  {{ memberRegion(member) === 'VIP' ? $t('enterprise_management_china') : $t('enterprise_management_international') }}
                </span>
              </div>

              <div>
                <span class="member-status" :class="`is-${memberState(member)}`">
                  {{ memberStatusLabel(member) }}
                </span>
              </div>

              <div class="join-time-cell">
                {{ memberTimeLabel(member) }}
              </div>

              <div v-if="memberState(member) === 'member'" class="permission-cell">
                <div class="permission-line">
                  <span class="permission-label">{{ $t('account_enterprise_table_header_file_permission') }}</span>
                  <template v-if="getMemberFilePermission(member).length">
                    <span v-for="item in getMemberFilePermission(member)" :key="item" class="permission-value">
                      <WorkbenchIcon name="check" :size="11" />{{ item }}
                    </span>
                  </template>
                  <span v-else class="permission-empty">—</span>
                </div>
                <div class="permission-line">
                  <span class="permission-label">{{ $t('account_enterprise_table_header_art_permission') }}</span>
                  <template v-if="getMemberArtPermission(member).length">
                    <span v-for="item in getMemberArtPermission(member)" :key="item" class="permission-value">
                      <WorkbenchIcon name="check" :size="11" />{{ item }}
                    </span>
                  </template>
                  <span v-else class="permission-empty">—</span>
                </div>
              </div>
              <div v-else class="permission-empty">—</div>

              <div class="operation-cell">
                <button
                  v-for="operation in getMemberOperations(member)"
                  :key="operation.key"
                  type="button"
                  :class="['operation-button', { 'is-danger': ['delete', 'quit', 'revoke', 'remove-invite'].includes(operation.key) }]"
                  @click="handleOperation(operation.key, member)"
                >
                  {{ operation.label }}
                </button>
                <span v-if="!getMemberOperations(member).length" class="operation-empty">—</span>
              </div>
            </div>
          </div>

          <div v-else class="enterprise-empty-state">
            <span class="enterprise-empty-icon">
              <WorkbenchIcon name="building-2" :size="28" />
            </span>
            <h2>{{ hasEnterprise ? $t('enterprise_management_no_members') : $t('account_enterprise_table_not_data_title') }}</h2>
            <p>{{ memberEmptyDescription }}</p>
            <button v-if="!hasEnterprise" class="enterprise-button enterprise-button-primary empty-create-button" type="button" @click="handleCreateEnterprise">
              <WorkbenchIcon name="plus" :size="14" />
              {{ $t('account_enterprise_table_not_data_create') }}
            </button>
          </div>
        </div>
      </div>

      <footer v-if="tableData.length" class="enterprise-table-footer">
        <span>{{ memberCounts.expired ? $t('enterprise_ui_member_summary', { count: memberCounts.totalMembers, expired: memberCounts.expired }) : $t('enterprise_management_member_count', { count: memberCounts.totalMembers }) }}</span>
        <el-pagination
          v-model:current-page="params.pageNo"
          :page-size="params.pageSize"
          :background="false"
          layout="prev, pager, next"
          :total="tableData.length"
          :pager-count="5"
          @current-change="handleCurrentChange"
        />
      </footer>
    </template>
  </section>
</template>

<script setup lang="ts">
import CreateDialog from '../Dialog/CreateDialog.vue'
import AddMemberDialog from './AddMemberDialog.vue'
import WorkbenchIcon from '@/components/Workbench/WorkbenchIcon.vue'
import AccountServer from '~/services/AccountServer'
import UserServer from '~/services/UserServer'
import type { IUserInfo } from '~/services/UserServer/types'
import GlobalBizServer from '~/services/GlobalBizServer'
import {
  EnterpriseMemberPermission,
  PermissionUtils,
  type EnterpriseInfo,
  type EnterpriseMember,
  type EnterpriseTargetInvite,
} from '~/services/AccountServer/types'
import type { GlobalBizMember, GlobalBizSite, GlobalBizSpaceSummary } from '~/services/GlobalBizServer/types'
import { NetCode } from '~/types/const'
import useUserStore from '~/store/modules/user'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import Toast from '~/utils/toast'
import EventBus from 'vue3-eventbus'
import { useSiteConfig } from '~/config/useSiteConfig'
import { useLegacyEnterpriseWorkspace } from '~/composables/useLegacyEnterpriseWorkspace'
import LegacyServer from '~/services/LegacyEnterpriseServer'
import { legacyWorkspaceQuotaUsage, legacyWorkspaceSeats } from '~/utils/legacyEnterpriseWorkspace'
import { enterpriseJoinStorageKey, readEnterpriseJoinRecord, saveEnterpriseJoinRecord, runEnterpriseJoin, type EnterpriseJoinRecord } from '~/utils/legacyEnterpriseJoin'
import {
  getEnterpriseMemberRefreshKeys,
  getEnterpriseManagementAccess,
  getEnterpriseMemberCountSummary,
  getEnterpriseMemberState,
  getEnterpriseMemberOperationKeys,
  type EnterpriseInvitationState,
  type EnterpriseMemberOperationKey,
} from '~/utils/enterpriseManagement'
import { getEnterpriseQuotaSnapshot } from '~/utils/enterpriseTrade'
import type ProDialog from '~/components/Pro/ProDialog/ProDialog.vue'

type DisplayMember = EnterpriseMember & { region?: GlobalBizSite }
type OperationKey = EnterpriseMemberOperationKey

const createDialogRef = ref<InstanceType<typeof CreateDialog>>()
const addMemberDialogRef = ref<InstanceType<typeof AddMemberDialog>>()
const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const { $loginCom } = useNuxtApp()
const { userInfo, bizTeamInfo, userRights } = storeToRefs(userStore)
const { t } = useI18n()
const { isVipSite } = useSiteConfig()
const proDialogRef = inject('proDialogRef') as Ref<InstanceType<typeof ProDialog>>
const enterpriseBalanceAlert = useEnterpriseBalanceAlert()
const globalRegions: GlobalBizSite[] = ['VIP', 'AI']

const tableData = ref<DisplayMember[]>([])
const globalSpaces = ref<GlobalBizSpaceSummary[]>([])
const invitedInfo = ref<{ inviteBizTeamId?: number; bizTeamName?: string }>({})
const joiningEnterprise = ref(false)
const joiningRecord = ref<EnterpriseJoinRecord | null>(null)
const loadingMembers = ref(false)
let memberRequestRevision = 0
let joinRequestRevision = 0
const params = reactive({
  pageSize: 5,
  pageNo: 1,
})

const currentRegion = computed<GlobalBizSite>(() => isVipSite.value ? 'VIP' : 'AI')
const joinStorageKey = computed(() => enterpriseJoinStorageKey(userInfo.value?.id, invitedInfo.value.inviteBizTeamId, currentRegion.value))
const joinScopeKey = computed(() => `${userStore.token || ''}:${joinStorageKey.value}`)
watch(joinScopeKey, () => {
  joinRequestRevision++
  joiningEnterprise.value = false
  joiningRecord.value = readEnterpriseJoinRecord(joinStorageKey.value)
}, { immediate: true, flush: 'sync' })
const isMemberInviteEntry = computed(() => String(route.query.menu || '') === 'enterprise' && String(route.query.source || '') === 'member-invite')
const currentSiteName = computed(() => t(isVipSite.value ? 'enterprise_ui_site_vip' : 'enterprise_ui_site_ai'))
const enterpriseName = computed(() => String(bizTeamInfo.value?.businessName || '').trim())
const managementAccess = computed(() => getEnterpriseManagementAccess(
  bizTeamInfo.value,
  userInfo.value?.bizTeamId,
  userInfo.value?.bizTeamMemberRole,
))
const hasEnterprise = computed(() => managementAccess.value.hasEnterprise)
const isLegacyCross = computed(() => managementAccess.value.workspaceMode === 'legacy-cross')
const isGlobalTeam = computed(() => managementAccess.value.workspaceMode !== 'local')
const workspace = useLegacyEnterpriseWorkspace(isLegacyCross)
const legacySeats = computed(() => legacyWorkspaceSeats(workspace.summary.value))
const legacySeatBreakdown = computed(() => legacySeats.value?.spaces.map(space =>
  `${space.site === 'VIP' ? t('enterprise_management_china') : t('enterprise_management_international')} ${t('enterprise_management_seats_used', { used: space.usedSeats, total: legacySeats.value?.seatLimit || 0 })}`,
).join(' · '))
const canInviteOrAddMembers = computed(() => managementAccess.value.canInviteOrAdd)
const canDissolveEnterprise = computed(() => managementAccess.value.canDissolve)
const canManageBalanceAlert = computed(() => managementAccess.value.canManageBalanceAlert)
const canPurchaseFuel = computed(() => managementAccess.value.canPurchaseFuel)
const showSeatSummary = computed(() => managementAccess.value.showSeats)
const showWorkspaceQuota = computed(() => managementAccess.value.showWorkspaceQuota)
const showIndependentQuota = computed(() => managementAccess.value.hasActivePlan && managementAccess.value.workspaceMode === 'local')
const independentQuota = computed(() => getEnterpriseQuotaSnapshot(bizTeamInfo.value))
const memberCounts = computed(() => getEnterpriseMemberCountSummary(tableData.value))
const memberEmptyDescription = computed(() => {
  if (!hasEnterprise.value) return t('enterprise_management_create_description')
  if (!managementAccess.value.hasActivePlan) return t('enterprise_management_buy_plan_description')
  return t('enterprise_management_add_member_description')
})
const usedSeatCount = computed(() => {
  if (isLegacyCross.value) return legacySeats.value?.usedSeats ?? null
  if (memberCounts.value.totalMembers) return memberCounts.value.seatUsed
  if (isGlobalTeam.value && globalSpaces.value.length) return globalSpaces.value.reduce((sum, item) => sum + Number(item.seatCount || 0), 0)
  return Number(bizTeamInfo.value?.memberCount || 0)
})
const seatLimit = computed(() => isLegacyCross.value ? legacySeats.value?.seatLimit ?? null : Number(
  (isGlobalTeam.value
    ? bizTeamInfo.value?.globalSeatLimit || globalSpaces.value.find(item => item.seatLimit)?.seatLimit
    : userRights.value?.memberCountLimit)
  || Math.max(usedSeatCount.value || 0, 1),
))
const pagedMembers = computed(() => {
  const start = (params.pageNo - 1) * params.pageSize
  return tableData.value.slice(start, start + params.pageSize)
})
const enterpriseMemberRefreshKeys = computed(() => getEnterpriseMemberRefreshKeys({
  token: userStore.token,
  userId: userInfo.value?.id,
  bizTeamId: userInfo.value?.bizTeamId,
  enterpriseId: bizTeamInfo.value?.id,
  serviceLevel: bizTeamInfo.value?.serviceLevel,
  serviceEndTime: bizTeamInfo.value?.serviceEndTime,
  globalContractNo: bizTeamInfo.value?.globalContractNo,
  regionMode: bizTeamInfo.value?.regionMode,
  legacyMigrationNo: bizTeamInfo.value?.legacyMigrationNo,
  currentRegion: currentRegion.value,
  menu: String(route.query.menu || ''),
}))

watch(
  [
    () => enterpriseMemberRefreshKeys.value.scopeKey,
    () => enterpriseMemberRefreshKeys.value.refreshKey,
  ],
  ([scopeKey], [previousScopeKey]) => {
    memberRequestRevision++
    loadingMembers.value = false
    if (scopeKey !== previousScopeKey) {
      tableData.value = []
      globalSpaces.value = []
    }
    if (String(route.query.menu || '').includes('enterprise') && userStore.token) void getEnterpriseInfo()
  },
  { flush: 'sync' },
)

function formatQuota(value?: number | string | null) {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'string' && /^\d+$/.test(value)) return BigInt(value).toLocaleString('en-US')
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value.toLocaleString('en-US') : '—'
}

function quotaPercent(value?: number | string | null, total?: number | string | null) {
  const normalizedValue = Number(value || 0)
  const normalizedTotal = Number(total || 0)
  if (!Number.isFinite(normalizedValue) || !Number.isFinite(normalizedTotal) || normalizedTotal <= 0) return 0
  return Math.min(100, Math.max(0, normalizedValue / normalizedTotal * 100))
}

function globalQuota(region: GlobalBizSite) {
  if (isLegacyCross.value) {
    const quota = legacyWorkspaceQuotaUsage(workspace.summary.value, region)
    const total = quota?.total ?? null
    const used = quota?.used ?? null
    return {
      total,
      used,
      packageRemaining: quota?.packageRemaining ?? null,
      percent: quotaPercent(used, total),
    }
  }
  const summary = globalSpaces.value.find(item => item.region === region)
  const total = summary?.totalQuota ?? null
  const remaining = summary?.remainQuota ?? null
  const used = total !== null && remaining !== null && remaining <= total ? total - remaining : null
  return {
    total,
    used,
    packageRemaining: summary?.packageRemainQuota ?? null,
    percent: quotaPercent(used, total),
  }
}

function memberKey(member: DisplayMember) {
  return `${member.recordType || 'MEMBER'}-${member.region || currentRegion.value}-${member.id}`
}

function memberRegion(member: DisplayMember): GlobalBizSite {
  return member.region || currentRegion.value
}

function roleLabel(roleType: number) {
  if (roleType === 1) return t('create_enterprise_table_header_creator')
  if (roleType === 2) return t('account_enterprise_add_member_role_admin')
  return t('account_enterprise_table_header_member')
}

function formatMemberDate(value?: string | null) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  const pad = (item: number) => String(item).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function pendingTimeLabel(member: DisplayMember) {
  const time = formatMemberDate(member.createTime)
  return time === '—' ? t('enterprise_management_waiting_accept') : t('enterprise_management_invited_at', { time })
}

function memberState(member: DisplayMember): EnterpriseInvitationState {
  return getEnterpriseMemberState(member)
}

function memberStatusLabel(member: DisplayMember) {
  const state = memberState(member)
  if (state === 'pending') return t('enterprise_management_pending')
  if (state === 'expired') return t('enterprise_management_expired')
  return t('account_enterprise_table_header_status_success')
}

function memberTimeLabel(member: DisplayMember) {
  const state = memberState(member)
  if (state === 'pending') return pendingTimeLabel(member)
  if (state === 'expired') return member.expireTime
    ? t('enterprise_management_expired_at', { time: formatMemberDate(member.expireTime) })
    : t('enterprise_management_expired')
  return formatMemberDate(member.confirmAddTime || member.createTime)
}

function getMemberFilePermission(member: DisplayMember) {
  const permissions: string[] = []
  if (PermissionUtils.hasPermission(member.permission || 0, EnterpriseMemberPermission.FILE_LOOK)) {
    permissions.push(t('account_enterprise_permission_look'))
  }
  if (PermissionUtils.hasPermission(member.permission || 0, EnterpriseMemberPermission.FILE_DOWNLOAD)) {
    permissions.push(t('account_enterprise_permission_download'))
  }
  return permissions
}

function getMemberArtPermission(member: DisplayMember) {
  const permissions: string[] = []
  if (PermissionUtils.hasPermission(member.permission || 0, EnterpriseMemberPermission.TERM_USE)) {
    permissions.push(t('account_enterprise_permission_term_use'))
  }
  if (PermissionUtils.hasPermission(member.permission || 0, EnterpriseMemberPermission.TERM_EDIT)) {
    permissions.push(t('account_enterprise_permission_edit'))
  }
  return permissions
}

function getMemberOperations(member: DisplayMember): Array<{ key: OperationKey; label: string }> {
  const isSelf = Number(userInfo.value?.id) === Number(member.memberUid)
  return getEnterpriseMemberOperationKeys({
    readonly: managementAccess.value.readonlyMembers,
    operatorRole: Number(userInfo.value?.bizTeamMemberRole),
    targetRole: Number(member.roleType),
    isSelf,
    invitationState: member.recordType === 'INVITATION' ? memberState(member) : 'member',
    hasInviteUrl: Boolean(String(member.inviteUrl || '').trim()),
  }).map((key) => ({
    key,
    label: key === 'edit'
      ? t('enterprise_management_edit_member')
      : key === 'quit'
        ? t('account_enterprise_quit_title')
        : key === 'revoke'
          ? t('enterprise_management_revoke')
          : key === 'copy-link'
            ? t('enterprise_management_copy')
          : key === 'readd'
            ? t('enterprise_management_readd')
            : key === 'remove-invite'
              ? t('enterprise_management_remove_record')
              : memberState(member) === 'pending'
                ? t('enterprise_management_revoke')
                : t('account_enterprise_operation_delete'),
  }))
}

function normalizeGlobalMembers(globalMembers: GlobalBizMember[], localMembers: EnterpriseMember[]): DisplayMember[] {
  return globalMembers.map((member) => {
    const local = member.region === currentRegion.value
      ? localMembers.find(item => Number(item.id) === Number(member.id) || item.userAccount === member.userAccount)
      : undefined
    return {
      aliasName: local?.aliasName ?? member.aliasName ?? null,
      bizTeamId: member.bizTeamId,
      confirmAddTime: local?.confirmAddTime || member.confirmAddTime || '',
      costWords: local?.costWords || 0,
      createTime: local?.createTime || member.createTime || '',
      id: member.id,
      memberUid: member.memberUid || local?.memberUid || 0,
      permission: local?.permission ?? member.permission ?? 0,
      roleType: member.roleType,
      status: member.status,
      updateTime: local?.updateTime || member.updateTime || '',
      userAccount: member.userAccount,
      region: member.region,
      recordType: member.recordType,
      inviteStatus: member.inviteStatus,
      invitationStatus: member.invitationStatus,
      invitationId: member.invitationId,
      inviteTime: member.inviteTime,
      expireTime: member.expireTime,
      inviteUrl: member.inviteUrl,
      token: member.token,
    }
  })
}

function normalizeTargetInvites(invites: EnterpriseTargetInvite[]): DisplayMember[] {
  return invites
    .filter(invite => invite.status === 'PENDING' || invite.status === 'EXPIRED')
    .map(invite => ({
      aliasName: null,
      bizTeamId: Number(bizTeamInfo.value?.id || userInfo.value?.bizTeamId || 0),
      confirmAddTime: '',
      costWords: 0,
      createTime: invite.createTime || '',
      id: invite.id,
      invitationId: invite.id,
      memberUid: 0,
      permission: 0,
      roleType: 3,
      status: 0,
      updateTime: invite.createTime || '',
      userAccount: invite.email || invite.inviteAccount || '',
      region: invite.region,
      recordType: 'INVITATION',
      inviteStatus: invite.status,
      inviteUrl: invite.url,
      token: invite.token,
      expireTime: invite.expireTime,
    }))
}

async function requestMemberList() {
  if (!hasEnterprise.value || loadingMembers.value) return
  const revision = memberRequestRevision
  loadingMembers.value = true
  try {
    const localResponse = await AccountServer.getEnterpriseMemberList()
    if (revision !== memberRequestRevision) return
    if (localResponse.code !== NetCode.SUCCESS) {
      Toast(localResponse.message)
      return
    }
    const localMembers = (localResponse.list || []).sort((a: EnterpriseMember, b: EnterpriseMember) => a.roleType - b.roleType)
    tableData.value = localMembers.map(member => ({ ...member, region: currentRegion.value }))
    if (!isGlobalTeam.value) globalSpaces.value = []

    if (managementAccess.value.workspaceMode === 'global' && bizTeamInfo.value?.globalContractNo) {
      try {
          const globalResponse = await GlobalBizServer.members(bizTeamInfo.value.globalContractNo)
          if (revision !== memberRequestRevision) return
        if (globalResponse.code === NetCode.SUCCESS && globalResponse.data) {
          globalSpaces.value = globalResponse.data.spaces || []
          if (globalResponse.data.members?.length) {
            tableData.value = normalizeGlobalMembers(globalResponse.data.members, localMembers)
              .sort((a, b) => a.roleType - b.roleType)
          }
        }
      } catch {
        // 非全球套餐所有者或远端暂不可用时，继续展示当前站点已有成员。
      }
    }
    try {
      const inviteResponse = await AccountServer.getEnterpriseInviteLinks()
      if (revision !== memberRequestRevision) return
      if (inviteResponse.code === NetCode.SUCCESS) {
        tableData.value = [...tableData.value, ...normalizeTargetInvites(inviteResponse.data?.dedicatedInvites || [])]
      }
    } catch {
      // 邀请记录临时不可用时仍保留已加入成员，避免整个成员页空白。
    }
    const maxPage = Math.max(1, Math.ceil(tableData.value.length / params.pageSize))
    if (params.pageNo > maxPage) params.pageNo = maxPage
  } finally {
    if (revision === memberRequestRevision) loadingMembers.value = false
  }
}

async function getEnterpriseInfo() {
  if (userInfo.value?.inviteBizTeamId && userInfo.value?.bizTeamName) {
    invitedInfo.value = {
      inviteBizTeamId: userInfo.value.inviteBizTeamId,
      bizTeamName: userInfo.value.bizTeamName,
    }
    return
  }
  invitedInfo.value = {}
  if (hasEnterprise.value) {
    if (!bizTeamInfo.value?.id) await userStore.getBizTeamInfo()
    await requestMemberList()
  } else {
    tableData.value = []
    globalSpaces.value = []
  }
}

function handleCurrentChange(page: number) {
  params.pageNo = page
}

function handleCreateEnterprise() {
  createDialogRef.value?.open(0)
}

function handleAddMember() {
  addMemberDialogRef.value?.open()
}

function openEnterprisePlans() {
  proDialogRef.value?.open({ mode: 1, pricingMode: isGlobalTeam.value ? 'global' : 'local' })
}

function openBalanceAlert() {
  enterpriseBalanceAlert.openDialog()
}

function handleDissolveEnterprise() {
  createDialogRef.value?.open(4)
}

async function handleJoinEnterprise() {
  const invitedBizTeamId = Number(invitedInfo.value.inviteBizTeamId || 0)
  if (!invitedBizTeamId || joiningEnterprise.value) return
  const revision = ++joinRequestRevision
  const scope = joinScopeKey.value
  const storageKey = joinStorageKey.value
  const isCurrent = () => revision === joinRequestRevision && scope === joinScopeKey.value
  let joinedUser: IUserInfo | undefined
  let joinedEnterprise: EnterpriseInfo | undefined
  joiningEnterprise.value = true
  try {
    const outcome = await runEnterpriseJoin({
      record: joiningRecord.value,
      createRequestId: createLegacyJoinRequestId,
      confirm: requestId => AccountServer.joinEnterprise({ bizTeamId: invitedBizTeamId, requestId }),
      query: requestId => LegacyServer.memberStatus(invitedBizTeamId, requestId),
      isCurrent,
      remember: record => {
        if (!isCurrent()) return
        joiningRecord.value = record
        saveEnterpriseJoinRecord(storageKey, record)
      },
      synchronize: async canApply => {
        const userResponse = await UserServer.getUserInfo<IUserInfo>()
        if (!canApply() || userResponse.code !== NetCode.SUCCESS
          || Number(userResponse.data?.bizTeamId || 0) !== invitedBizTeamId) return false
        // 同一轮用户和企业信息准备完再应用，避免半刷新状态触发重复加入。
        const enterpriseResponse = await AccountServer.getEnterpriseInfo(invitedBizTeamId)
        if (!canApply() || enterpriseResponse.code !== NetCode.SUCCESS
          || Number(enterpriseResponse.data?.id || 0) !== invitedBizTeamId) return false
        joinedEnterprise = enterpriseResponse.data
        joinedUser = userResponse.data
        return true
      },
    })
    if (!isCurrent() || outcome.state === 'cancelled') return
    if (outcome.state !== 'success') {
      Toast(outcome.message || t(outcome.state === 'failed'
        ? 'enterprise_management_invite_join_failed' : 'enterprise_management_processing_hint'))
      return
    }
    if (joinedEnterprise && joinedUser) {
      userStore.isUserInfoReady = true
      bizTeamInfo.value = joinedEnterprise
      userInfo.value = joinedUser
    }
    invitedInfo.value = {}
    await getEnterpriseInfo()
  } catch (error: any) {
    if (isCurrent()) Toast(error?.message || t('enterprise_management_invite_join_failed'))
  } finally {
    if (isCurrent()) joiningEnterprise.value = false
  }
}

function createLegacyJoinRequestId() {
  return `LMCONFIRM_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
}

async function removeGlobalMember(member: DisplayMember) {
  if (!bizTeamInfo.value?.globalContractNo) return false
  const response = await GlobalBizServer.removeMember({
    contractNo: bizTeamInfo.value.globalContractNo,
    region: memberRegion(member),
    memberId: member.id,
  })
  if (response.code !== NetCode.SUCCESS) {
    Toast(response.message)
    return false
  }
  return true
}

function handleOperation(operation: OperationKey, member: DisplayMember) {
  if (operation === 'edit') addMemberDialogRef.value?.open(member)
  if (operation === 'readd') addMemberDialogRef.value?.openInvite(member)
  if (operation === 'revoke' || operation === 'remove-invite') void removeInvitation(member)
  if (operation === 'copy-link') void copyMemberInviteUrl(member)
  if (operation === 'delete') {
    const isRemoteGlobalMember = managementAccess.value.workspaceMode === 'global' && memberRegion(member) !== currentRegion.value
    createDialogRef.value?.open(3, {
      ...member,
      deleteRegionLabel: memberRegion(member) === 'VIP' ? t('enterprise_management_china') : t('enterprise_management_international'),
      deleteAction: isRemoteGlobalMember ? () => removeGlobalMember(member) : undefined,
    })
  }
  if (operation === 'quit') createDialogRef.value?.open(5)
}

/** 复制待接受链接邀请的地址，关闭邀请弹窗后仍可在成员列表再次分发 */
async function copyMemberInviteUrl(member: DisplayMember) {
  const url = String(member.inviteUrl || '').trim()
  if (!url) return
  try {
    await navigator.clipboard.writeText(url)
    Toast(t('enterprise_management_copy_success'))
  } catch {
    Toast(t('enterprise_management_copy_failed'))
  }
}

async function removeInvitation(member: DisplayMember) {
  const invitationId = Number(member.invitationId || member.id)
  if (!Number.isFinite(invitationId)) return
  try {
    const response = memberState(member) === 'pending'
      ? await AccountServer.revokeEnterpriseTargetInvite({ inviteId: invitationId, region: memberRegion(member) })
      : await AccountServer.removeEnterpriseTargetInvite({ inviteId: invitationId, region: memberRegion(member) })
    if (response.code !== NetCode.SUCCESS) {
      Toast(response.message)
      return
    }
    Toast(operationSuccessText(member))
    refreshMembers()
  } catch (error: any) {
    Toast(error?.message || t('enterprise_management_operation_failed'))
  }
}

function operationSuccessText(member: DisplayMember) {
  return memberState(member) === 'pending'
    ? t('enterprise_management_revoke_success')
    : t('enterprise_management_remove_success')
}

function refreshMembers() {
  void requestMemberList()
  if (isLegacyCross.value) void workspace.refresh()
}

async function leaveEnterpriseAndReturnHome() {
  userStore.userInfo.bizTeamId = undefined
  userStore.userInfo.bizTeamMemberRole = undefined
  await userStore.getUserInfo()
  router.push({ path: '/' })
}

function handleDissolveEnterpriseCall() {
  void leaveEnterpriseAndReturnHome()
}

function handleQuitEnterpriseCall() {
  void leaveEnterpriseAndReturnHome()
}

async function handleInviteLoginSuccess() {
  if (!isMemberInviteEntry.value) return
  await userStore.getUserInfo()
  if (userInfo.value?.bizTeamId) await userStore.getBizTeamInfo()
  await getEnterpriseInfo()
}

onMounted(() => {
  if (isMemberInviteEntry.value && !userStore.token) $loginCom.open()
  else void getEnterpriseInfo()
  EventBus.on('createAccountEnterprise', getEnterpriseInfo)
  EventBus.on('loginSuccess', handleInviteLoginSuccess)
})

onUnmounted(() => {
  memberRequestRevision++
  joinRequestRevision++
  EventBus.off('createAccountEnterprise', getEnterpriseInfo)
  EventBus.off('loginSuccess', handleInviteLoginSuccess)
})
</script>

<style scoped lang="scss">
.enterprise-page {
  --enterprise-primary: #2b7de9;
  --enterprise-ink: #1f2329;
  --enterprise-sub: #51565d;
  --enterprise-muted: #98a2b3;
  --enterprise-line: #e8ebf0;
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 18px;
  color: var(--enterprise-ink);
}

.enterprise-toolbar {
  display: flex;
  min-height: 34px;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.enterprise-title {
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  line-height: 24px;
}

.global-title-suffix { color: var(--enterprise-ink); }

.enterprise-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.enterprise-button {
  display: inline-flex;
  height: 34px;
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 16px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: #fff;
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color .18s ease, border-color .18s ease, color .18s ease;
}

.enterprise-button-primary {
  border-color: var(--enterprise-primary);
  background: var(--enterprise-primary);
  color: #fff;
}

.enterprise-button-primary:hover {
  border-color: #216fd4;
  background: #216fd4;
}

.enterprise-button:disabled {
  cursor: wait;
  opacity: .65;
  pointer-events: none;
}

.enterprise-button-secondary {
  border-color: var(--enterprise-primary);
  color: var(--enterprise-primary);
}

.enterprise-button-secondary:hover { background: #f5f9ff; }

.enterprise-button-danger {
  border-color: #e5484d;
  color: #e5484d;
}

.enterprise-button-danger:hover { background: #fff6f6; }

.enterprise-overview {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.section-title {
  margin: 0 0 2px;
  font-size: 15px;
  font-weight: 800;
  line-height: 22px;
}

.seat-summary {
  display: flex;
  min-height: 42px;
  box-sizing: border-box;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 10px;
  background: #f7f9fc;
  color: var(--enterprise-sub);
  font-size: 12.5px;
  font-weight: 600;
}

.seat-summary__main {
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: 10px;
}

.seat-summary__main > .workbench-icon { color: var(--enterprise-primary); }

.seat-summary { flex-wrap: wrap; }
.seat-summary .legacy-seat-breakdown { flex-basis: 100%; margin-left: 23px; white-space: normal; line-height: 1.6; }
.workspace-refresh { border: 0; background: transparent; color: var(--enterprise-primary); cursor: pointer; font: inherit; font-size: 12px; }
.workspace-refresh:disabled { opacity: .5; cursor: wait; }

.seat-summary small {
  margin-left: auto;
  color: var(--enterprise-muted);
  font-size: 11.5px;
  font-weight: 400;
}

.space-quota-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.local-quota-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }

.quota-card {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 10px;
  padding: 16px 18px;
  border: 1px solid var(--enterprise-line);
  border-radius: 12px;
  background: #fff;
}

.quota-card__header,
.quota-card__footer {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.quota-card__header { color: var(--enterprise-sub); font-size: 12.5px; font-weight: 700; }
.quota-card__header strong { color: var(--enterprise-primary); font-size: 13px; }
.quota-card__footer { color: var(--enterprise-muted); font-size: 11.5px; }

.quota-card__title {
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
}

.quota-card__title b {
  color: var(--enterprise-ink);
  font-size: 13.5px;
  font-weight: 800;
}

.quota-card__title small {
  overflow: hidden;
  color: var(--enterprise-muted);
  font-size: 11.5px;
  font-weight: 400;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.quota-progress {
  width: 100%;
  height: 6px;
  overflow: hidden;
  border-radius: 999px;
  background: #e8eef7;
}

.quota-progress > span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--enterprise-primary);
  transition: width .2s ease;
}

.quota-link {
  flex: 0 0 auto;
  padding: 0;
  border: 0;
  color: var(--enterprise-primary);
  background: transparent;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}

.space-quota-card {
  display: flex;
  min-height: 48px;
  box-sizing: border-box;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 16px;
  border-radius: 10px;
  background: #f7f9fc;
}

.space-quota-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--enterprise-ink);
  font-size: 12.5px;
  font-weight: 700;
}

.space-quota-label .workbench-icon { color: var(--enterprise-primary); }

.space-quota-card strong {
  color: var(--enterprise-primary);
  font-size: 12.5px;
  font-weight: 700;
}

.space-quota-value {
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  text-align: right;
}

.space-quota-value small {
  color: var(--enterprise-muted);
  font-size: 11px;
  font-weight: 400;
}

.enterprise-table-shell {
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--enterprise-line);
  border-radius: 10px;
  background: #fff;
}

.enterprise-table-scroll {
  min-width: 100%;
  overflow-x: auto;
}

.enterprise-table-grid {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) 110px 90px 160px 260px 160px;
  align-items: center;
  gap: 10px;
  min-width: 980px;
}

.is-global .enterprise-table-grid {
  grid-template-columns: minmax(200px, 1fr) 110px 130px 90px 160px 170px 160px;
  min-width: 1080px;
}

.enterprise-table-header {
  padding: 10px 16px;
  background: #f5f9ff;
  color: var(--enterprise-sub);
  font-size: 12px;
  font-weight: 600;
}

.enterprise-table-row {
  min-height: 66px;
  box-sizing: border-box;
  padding: 12px 16px;
  border-bottom: 1px solid #f2f5f9;
  color: var(--enterprise-sub);
  font-size: 12px;
}

.enterprise-table-row:last-child { border-bottom: 0; }

.member-cell,
.permission-line,
.operation-cell {
  display: flex;
  min-width: 0;
  align-items: center;
}

.member-cell { gap: 8px; }

.member-avatar {
  display: inline-flex;
  width: 26px;
  height: 26px;
  flex: 0 0 26px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #5b4fe9;
  color: #fff;
}

.member-avatar-role-2 { background: #f59e0b; }
.member-avatar-role-3 { background: #2b7de9; }

.member-account {
  overflow: hidden;
  color: var(--enterprise-ink);
  font-size: 12.5px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.role-tag {
  flex: 0 0 auto;
  padding: 2px 8px;
  border-radius: 8px;
  background: #f2f4f7;
  color: var(--enterprise-sub);
  font-size: 10.5px;
  font-weight: 600;
}

.muted-cell,
.join-time-cell {
  overflow: hidden;
  color: var(--enterprise-muted);
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.space-tag {
  display: inline-flex;
  padding: 3px 8px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 600;
}

.space-tag-vip {
  background: #eaf3ff;
  color: #176fd1;
}

.space-tag-ai {
  background: #efedff;
  color: #5648dc;
}

.member-status {
  font-size: 12.5px;
  font-weight: 600;
}

.member-status.is-active { color: #22a06b; }
.member-status.is-member { color: #22a06b; }
.member-status.is-pending { color: #f59e0b; }
.member-status.is-expired { color: #98a2b3; }

.permission-cell {
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.permission-line { gap: 8px; }

.permission-label {
  min-width: 48px;
  color: var(--enterprise-muted);
  font-size: 11.5px;
}

.permission-value {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: var(--enterprise-sub);
  font-size: 11.5px;
}

.permission-value .workbench-icon { color: #22a06b; }
.permission-empty,
.operation-empty { color: var(--enterprise-muted); }

.operation-cell { flex-wrap: wrap; gap: 10px; }

.operation-button {
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--enterprise-primary);
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.operation-button.is-danger { color: #e5484d; }
.operation-button:hover { text-decoration: underline; }

.enterprise-empty-state {
  display: flex;
  min-height: 253px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 24px;
  box-sizing: border-box;
  text-align: center;
}

.enterprise-empty-icon {
  display: inline-flex;
  width: 54px;
  height: 54px;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: #f2f6fb;
  color: #aab3c1;
}

.enterprise-empty-state h2 {
  margin: 12px 0 0;
  color: var(--enterprise-sub);
  font-size: 13px;
  font-weight: 600;
}

.enterprise-empty-state p {
  margin: 6px 0 0;
  color: var(--enterprise-muted);
  font-size: 12px;
}

.empty-create-button { margin-top: 20px; }

.enterprise-table-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--enterprise-muted);
  font-size: 11.5px;
}

.enterprise-table-footer :deep(.el-pagination) {
  --el-pagination-button-width: 26px;
  --el-pagination-button-height: 26px;
  --el-pagination-button-disabled-bg-color: #fff;
  --el-pagination-bg-color: #fff;
  --el-pagination-hover-color: var(--enterprise-primary);
  padding: 0;
}

.enterprise-table-footer :deep(.btn-prev),
.enterprise-table-footer :deep(.btn-next),
.enterprise-table-footer :deep(.number) {
  min-width: 26px;
  border: 1px solid #e3e7ec;
  border-radius: 6px;
}

.invited-enterprise-card {
  display: flex;
  min-height: 330px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 36px;
  border: 1px solid var(--enterprise-line);
  border-radius: 10px;
  text-align: center;
}

.invited-enterprise-icon {
  display: inline-flex;
  width: 64px;
  height: 64px;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  background: #eaf3ff;
  color: var(--enterprise-primary);
}

.invited-enterprise-card h2 {
  margin: 18px 0 0;
  font-size: 16px;
  font-weight: 700;
}

.invited-enterprise-card p {
  margin: 8px 0 0;
  color: var(--enterprise-muted);
  font-size: 12px;
}

.invited-enterprise-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 24px;
}

.enterprise-button-spinner {
  width: 13px;
  height: 13px;
  box-sizing: border-box;
  border: 2px solid rgba(255, 255, 255, .45);
  border-top-color: #fff;
  border-radius: 50%;
  animation: enterprise-button-spin .8s linear infinite;
}

.invited-enterprise-card .invited-enterprise-sync-hint {
  max-width: 420px;
  margin-top: 10px;
  color: var(--enterprise-muted);
  line-height: 1.6;
}

@keyframes enterprise-button-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 900px) {
  .enterprise-toolbar,
  .seat-summary {
    align-items: flex-start;
    flex-direction: column;
  }

  .enterprise-actions { flex-wrap: wrap; }
  .seat-summary small { margin-left: 0; }
  .space-quota-grid { grid-template-columns: 1fr; }
}
</style>
